/**
 * SSO MFA Verification Page — /sso/mfa?challenge={nonce}
 *
 * Shown when an SSO user (SAML or OIDC) has TOTP configured.
 * The browser is redirected here by the backend after successful IdP
 * authentication.  This page collects the TOTP code, POSTs to
 * POST /sso/verify-mfa, then stores the returned JWT and navigates.
 */
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, ShieldCheck, AlertTriangle } from "lucide-react";

type StatusState = "loading" | "ready" | "invalid" | "rate_limited" | "submitting" | "error";

export default function SsoMfaPage() {
  const params  = new URLSearchParams(window.location.search);
  const challenge = params.get("challenge") ?? "";
  const setupRequired = params.get("setup_required") === "1";
  const restartLogin = () => window.location.replace("/login");

  const [status,    setStatus]    = useState<StatusState>("loading");
  const [code,      setCode]      = useState("");
  const [codeErr,   setCodeErr]   = useState("");
  const [apiErr,    setApiErr]    = useState("");
  // When the server issues a retryChallenge we swap to it so the next
  // attempt uses a fresh nonce.
  const [activeChal, setActiveChal] = useState(challenge);

  // ── Verify challenge is still valid on mount ────────────────────────
  useEffect(() => {
    if (setupRequired || !activeChal) { setStatus("invalid"); return; }
    fetch(`/sso/mfa-status/${encodeURIComponent(activeChal)}`, { credentials: "include", cache: "no-store" })
      .then(r => {
        if (r.ok) setStatus("ready");
        else      setStatus("invalid");
      })
      .catch(() => setStatus("invalid"));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Submit ──────────────────────────────────────────────────────────
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (status === "submitting") return;
    setCodeErr("");
    setApiErr("");

    const trimmed = code.replace(/\s/g, "");
    if (!/^\d{6}$/.test(trimmed)) {
      setCodeErr("Enter the 6-digit code from your authenticator app.");
      return;
    }

    setStatus("submitting");
    try {
      const res = await fetch("/sso/verify-mfa", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ challenge: activeChal, totpCode: trimmed }),
      });

      if (res.ok) {
        if (res.headers.get("content-type")?.includes("application/json")) {
          const data = await res.json();
          // Embedded MFA completes server-side; only a safe completion route,
          // never a JWT, is returned to this browser.
          if (typeof data.completionUrl === "string" &&
              /^\/sso\/embed\/completion\/[a-zA-Z0-9_-]{20,128}$/.test(data.completionUrl)) {
            window.location.replace(data.completionUrl);
            return;
          }
          throw new Error("Invalid completion response");
        }
        // The server returns the same localStorage-handoff HTML as the
        // initial SSO flow — navigate the full page to it so the script
        // runs and drops the JWT + refresh token into localStorage.
        const html = await res.text();
        document.open();
        document.write(html);
        document.close();
        return;
      }

      const data = await res.json().catch(() => ({})) as Record<string, unknown>;

      if (res.status === 401 && data.code === "MFA_INVALID_CODE") {
        // Swap to the fresh retry nonce issued by the server.
        if (typeof data.retryChallenge === "string") setActiveChal(data.retryChallenge);
        setApiErr(typeof data.message === "string" ? data.message : "Invalid or expired TOTP code. Please try again.");
        setStatus("ready");
        setCode("");
        return;
      }

      if (data.code === "CHALLENGE_INVALID") {
        setStatus("invalid");
        return;
      }

      if (res.status === 429 || data.code === "MFA_RATE_LIMITED") {
        setApiErr(typeof data.message === "string" ? data.message : "Too many failed verification attempts. Please restart login.");
        setStatus("rate_limited");
        return;
      }

      setApiErr(typeof data.message === "string" ? data.message : "Verification failed. Please sign in again.");
      setStatus("error");
    } catch {
      setApiErr("Network error — please check your connection and try again.");
      setStatus("error");
    }
  }

  // ── Render ──────────────────────────────────────────────────────────
  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (status === "rate_limited") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-sm">
          <CardHeader className="text-center">
            <AlertTriangle className="w-10 h-10 mx-auto mb-2 text-red-500" />
            <CardTitle>Account Temporarily Locked</CardTitle>
            <CardDescription>
              {apiErr || "Too many failed verification attempts. Please restart login."}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center">
            <Button
              variant="outline"
               onClick={restartLogin}
            >
              Return to Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === "invalid" || status === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-sm">
          <CardHeader className="text-center">
            <AlertTriangle className="w-10 h-10 mx-auto mb-2 text-amber-500" />
             <CardTitle>{setupRequired ? "MFA enrollment required" : "Session Expired"}</CardTitle>
            <CardDescription>
               {setupRequired
                 ? "Your organization requires MFA, but your account is not enrolled. Contact your IT administrator to arrange secure enrollment or account recovery, then restart sign-in from the widget. No access has been granted."
                 : status === "error"
                ? apiErr || "An error occurred."
                : "Your MFA session has expired or is invalid."}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center">
            <Button
              variant="outline"
               onClick={restartLogin}
            >
              Return to Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <ShieldCheck className="w-10 h-10 mx-auto mb-2 text-primary" />
          <CardTitle>Two-Factor Verification</CardTitle>
          <CardDescription>
            Enter the 6-digit code from your authenticator app to complete sign in.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            {apiErr && (
              <Alert variant="destructive">
                <AlertDescription>{apiErr}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-1">
              <Label htmlFor="totp-code">Authentication Code</Label>
              <Input
                id="totp-code"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                autoComplete="one-time-code"
                placeholder="000000"
                value={code}
                onChange={e => {
                  setCode(e.target.value.replace(/\D/g, "").slice(0, 6));
                  setCodeErr("");
                  setApiErr("");
                }}
                className={`text-center text-xl tracking-widest font-mono ${codeErr ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                aria-invalid={!!codeErr}
                aria-describedby={codeErr ? "totp-err" : undefined}
                autoFocus
                disabled={status === "submitting"}
              />
              {codeErr && (
                <p id="totp-err" role="alert" className="text-xs text-red-500">
                   {codeErr}
                </p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={status === "submitting"}
            >
              {status === "submitting"
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying…</>
                : "Verify and Sign In"}
            </Button>

            <div className="text-center">
              <button
                type="button"
                className="text-xs text-muted-foreground underline-offset-2 hover:underline"
                 onClick={restartLogin}
              >
                Cancel — return to sign in
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
